/** Общие кусочки UI раздела «Себестоимость»: окно, ошибка ручки, загрузка, бейдж, поле формы. */
import { RefreshCw, X } from "lucide-react";
import React, { useId, useRef } from "react";

import { cn } from "@/lib/utils";
import { useDialogFocus } from "../ozon-analytics/useDialogFocus";
import { apiErrorText } from "./errors";
import { characteristicLabel } from "./format";

export function ErrorBox({ error, what }: { error: unknown; what: string }): React.ReactElement {
  return (
    <div
      role="alert"
      className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-400"
    >
      {what}: {apiErrorText(error)}
    </div>
  );
}

/** Приглушённая строка «Характеристика: …» под названием; нечего показывать — ничего. */
export function CharacteristicLine({
  characteristicRef,
  name,
}: {
  characteristicRef: string;
  name: string | null | undefined;
}): React.ReactElement | null {
  const label = characteristicLabel(characteristicRef, name);
  if (label === null) return null;
  return (
    <div className="text-xs text-muted-foreground" data-testid="characteristic-line">
      Характеристика: {label}
    </div>
  );
}

export function Loading({ text }: { text: string }): React.ReactElement {
  return (
    <div
      role="status"
      className="flex items-center gap-3 rounded-md border bg-muted/30 px-4 py-6 text-sm text-muted-foreground"
    >
      <RefreshCw className="size-4 animate-spin" aria-hidden />
      {text}
    </div>
  );
}

export function Badge({
  cls,
  children,
  title,
}: {
  cls: string;
  children: React.ReactNode;
  title?: string;
}): React.ReactElement {
  return (
    <span
      title={title}
      className={cn("inline-flex whitespace-nowrap rounded px-1.5 py-0.5 text-xs font-medium", cls)}
    >
      {children}
    </span>
  );
}

/** Модальное окно: подложка, заголовок, крестик; фокус/Esc/Tab — общий хук окон. */
export function Modal({
  title,
  onClose,
  children,
  wide = false,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  wide?: boolean;
}): React.ReactElement {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  useDialogFocus(dialogRef, closeRef, onClose);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 dark:bg-black/60" onClick={onClose}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={cn(
          "max-h-[90vh] w-full overflow-auto rounded-lg border bg-background p-4 shadow-xl",
          wide ? "max-w-3xl" : "max-w-lg",
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-start gap-3">
          <h3 id={titleId} className="text-base font-semibold">
            {title}
          </h3>
          <button
            type="button"
            onClick={onClose}
            ref={closeRef}
            className="ml-auto rounded p-1 hover:bg-accent"
            aria-label="Закрыть"
          >
            <X className="size-4" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

/** Поле формы: подпись, контрол, ошибка/подсказка под ним. */
export function Field({
  label,
  error,
  hint,
  children,
}: {
  label: string;
  error?: string | null;
  hint?: React.ReactNode;
  children: (id: string) => React.ReactNode;
}): React.ReactElement {
  const id = useId();
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="text-sm font-medium">
        {label}
      </label>
      {children(id)}
      {error ? (
        <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
      ) : hint ? (
        <p className="text-xs text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
}

export const selectCls =
  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
